import React, { FC, useCallback, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { Button } from 'reactstrap';
import Image from 'next/image';
import _ from 'lodash';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { CustomArrowProps, default as Slider, Settings } from 'react-slick';
import styles from './index.module.scss';
import { carouselButtonsProps } from '@/libs/interfaceProps/carouselButtonsProps';
import useSWR from 'swr';
import { fetcher } from '@/libs/fetcher/fetcher';
import { OrgCategory } from '@/libs/interfaceProps/clientListProps';
import Skeleton from './CarouselButtonsSkeleton';
import { useEnvContext } from '@/context/envContext';
import { pushGTMClickTags } from '@/libs/pushGTMTags/pushGTMTags';
const NextArrow = React.memo(function NextArrow(props: CustomArrowProps & {
  cdnEnv: string;
}) {
  const {
    className,
    onClick,
    cdnEnv
  } = props;
  const NextArrowIcon = `${cdnEnv}/rms/images/icons/right-arrow.svg`;
  if (props.onClick !== null) {
    return <div className={classnames(className, styles['tt-carousel'], styles['tt-next-arrow'])} onClick={onClick}>
    <Image src={NextArrowIcon} width={'28'} height={'28'} alt="next button" className={classnames(styles['tt-arrow-icon'])} />
   </div>;
  }
  return null;
});
const PrevArrow = React.memo(function PrevArrow(props: CustomArrowProps & {
  cdnEnv: string;
}) {
  const {
    className,
    onClick,
    cdnEnv
  } = props;
  const PrevArrowIcon = `${cdnEnv}/rms/images/icons/left-arrow.svg`;
  if (props.onClick !== null) {
    return <div className={classnames(className, styles['tt-carousel'], styles['tt-prev-arrow'])} onClick={onClick}>
    <Image src={PrevArrowIcon} width={'28'} height={'28'} alt="prev button" className={classnames(styles['tt-arrow-icon'])} />
   </div>;
  }
  return null;
});
const CarouselButtons: FC<carouselButtonsProps> = ({
  orgId,
  onClick,
  buttonId,
  carouselPage,
  lang
}) => {
  const {
    cdnEnv
  } = useEnvContext();

  // Moved useMemo hooks inside the component function
  const defaultSettings = useMemo<Settings>(() => ({
    infinite: false,
    dots: false,
    slidesToShow: 2,
    slidesToScroll: 1,
    variableWidth: true,
    accessibility: true,
    swipeToSlide: true,
    nextArrow: <NextArrow cdnEnv={cdnEnv} />,
    prevArrow: <PrevArrow cdnEnv={cdnEnv} />,
    responsive: [{
      breakpoint: 2561,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    }, {
      breakpoint: 600,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    }]
  }), [cdnEnv]);
  const disableDesktop = useMemo<Settings>(() => ({
    infinite: false,
    slidesToShow: 2,
    slidesToScroll: 1,
    variableWidth: true,
    accessibility: true,
    swipeToSlide: true,
    nextArrow: <NextArrow cdnEnv={cdnEnv} />,
    prevArrow: <PrevArrow cdnEnv={cdnEnv} />,
    responsive: [{
      breakpoint: 2561,
      settings: 'unslick'
    }, {
      breakpoint: 600,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    }]
  }), [cdnEnv]);
  let elasticButton = '';
  if (carouselPage === 'search') {
    elasticButton = styles['bt-elastic'];
  } else {
    elasticButton = '';
  }
  const {
    data,
    error,
    isLoading,
    mutate
  } = useSWR(orgId && lang ? `/hub/services/orgCategory?orgId=${orgId}&lang=${lang}` : null, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 600000,
    // 10 minutes
    onError: err => console.error('Failed to fetch categories:', err)
  });

  // Clear SWR cache on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // For useSWRInfinite, we need to clear the cache differently
      // Simply call mutate without arguments to clear this specific hook's cache
      mutate();
    };
  }, [mutate]);
  const handleButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    localStorage.setItem('lastCdnEnv', cdnEnv);
    pushGTMClickTags({
      click_element: 'div',
      click_closest_class: 'tt-carousel tt-next-arrow',
      click_section_ID: 'NONE',
      link_classes: 'filter-buttons tt-button-small-bright-short',
      link_id: 'NONE',
      link_text: e.currentTarget.textContent ?? 'NONE',
      link_url: 'NONE',
      click_category: 'filter',
      click_destination: `Org list filtered by ${e.currentTarget.value ?? 'unknown'}`,
      click_closest_href: 'NONE'
    });
    if (onClick) {
      onClick(e);
    }
  }, [onClick, cdnEnv]);
  if (error) {
    console.error('Error fetching categories:', error);
    return null;
  }
  if (isLoading) return <Skeleton />;
  if (!data || data?.found === 0) return null;
  const uniqueCategories = Array.from(new Set(data.body.map((category: OrgCategory) => category.docType))).map(docType => {
    return data.body.find((category: OrgCategory) => category.docType === docType);
  });
  uniqueCategories.unshift({
    docType: 'All'
  });
  const renderSlides = () => uniqueCategories.map(({
    docType
  }, index) => {
    const currentButtonId = _.camelCase(docType) + index;
    return <Button key={`${docType}-${index}`} id={currentButtonId} data-bdd={`${_.camelCase(docType)}-btn`} data-category="filter" className={classnames(styles['tt-button-small-bright-short'], styles['filter-buttons'], elasticButton, styles['contentTypeButton'], buttonId === currentButtonId ? styles['tt-button-small-bright-active'] : '')} value={docType} onClick={handleButtonClick} style={{
      minWidth: 35
    }}>
     {docType}
    </Button>;
  });
  return <div className={classnames(styles['carousel-wrapper'], 'carousel-parent')} data-sentry-component="CarouselButtons" data-sentry-source-file="CarouselButtons.tsx">
   {uniqueCategories.length <= 8 ? <Slider {...disableDesktop}>{renderSlides()}</Slider> : <Slider {...defaultSettings}>{renderSlides()}</Slider>}
  </div>;
};
export default CarouselButtons;